import {
  Box,
  Container,
  Divider,
  Drawer,
  Hidden,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { HEADER_HEIGHT, ProfileButton, TopBar } from "@knowfully/ui.common";
import { useCustomThemeContext } from "components/App/CustomThemeProvider";
import ErrorBoundary from "components/ErrorBoundary";
import OrganizationBreadcrumbsWrapper from "components/OrganizationBreadcrumbsWrapper";
import UserMenu from "layouts/TopBar/UserMenu";
import { makeStyles } from "helpers/StyleHelper";
import useTitle from "hooks/useTitle";
import React, { useState } from "react";
import { Link, Outlet, useMatch } from "react-router-dom";

export const drawerWidth = 240;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
  },
  drawer: {
    [theme.breakpoints.up("md")]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  toolbar: {
    minHeight: HEADER_HEIGHT,
  },
  switcher: {
    padding: theme.spacing(1),
    display: "flex",
  },
  drawerPaper: {
    width: drawerWidth,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3, 0),
  },
  icon: {
    fontSize: "1.5rem",
  },
  selected: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  selectedIcon: {
    color: "white",
  },
  topBar: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  profileButton: {
    color: "white",
  },
}));

export type MenuItem = {
  eventKey: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  divider?: boolean;
};

type SidebarMenuItemProps = MenuItem & {
  styles: ReturnType<typeof useStyles>;
  onNavigate: () => void;
};

const SidebarMenuItem = ({
  icon,
  label,
  path,
  divider,
  styles,
  onNavigate,
}: SidebarMenuItemProps) => {
  const isCurrentUrl = useMatch({ path, end: false });

  useTitle(isCurrentUrl ? label : undefined);

  return (
    <Link
      to={path}
      style={{
        textDecoration: "none",
        color: "inherit",
      }}
      onClick={onNavigate}
    >
      <ListItemButton sx={isCurrentUrl ? styles.selected : null} divider={divider}>
        <ListItemIcon
          sx={
            {
              ...styles.icon,
              ...(isCurrentUrl ? styles.selectedIcon : {}),
            } as any
          }
        >
          {icon}
        </ListItemIcon>
        <ListItemText primary={label} />
      </ListItemButton>
    </Link>
  );
};

type SidebarLayoutProps = {
  items: MenuItem[];
};

const SidebarLayout = React.memo(({ items }: SidebarLayoutProps) => {
  const styles = useStyles(useTheme());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const { logoImageUrl, logoAlternativeText } = useCustomThemeContext();

  const handleCloseDrawer = () => setDrawerOpen(false);

  return (
    <Box sx={styles.root}>
      <TopBar
        sx={styles.topBar}
        logoImageUrl={logoImageUrl}
        logoAlternativeText={logoAlternativeText}
        title={!isMobile && <OrganizationBreadcrumbsWrapper isMobile={false} />}
        actions={
          <ProfileButton
            onClick={(e: React.MouseEvent<HTMLElement>) => setProfileAnchor(e.currentTarget)}
            isOpen={!!profileAnchor}
            sx={styles.profileButton}
          />
        }
        isMenuOpen={drawerOpen}
        onMenuClick={() => setDrawerOpen((open) => !open)}
      />
      <UserMenu
        anchor={profileAnchor}
        handleClose={() => setProfileAnchor(null)}
      />

      <Box component="nav" sx={styles.drawer} aria-label="nav">
        <Hidden smUp implementation="css">
          <Drawer
            variant="temporary"
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen((open) => !open)}
            sx={styles.drawerPaper}
            PaperProps={{ sx: styles.drawerPaper }}
            ModalProps={{ keepMounted: true }}
          >
            <Box
              sx={{ ...styles.toolbar, ...styles.switcher } as SxProps<Theme>}
            />
            <Box mx={1} my={2}>
              <OrganizationBreadcrumbsWrapper isMobile={true} />
            </Box>

            <Divider />

            <List component="nav">
              {items.map((item) => (
                <SidebarMenuItem
                  key={item.eventKey}
                  {...item}
                  styles={styles}
                  onNavigate={handleCloseDrawer}
                />
              ))}
            </List>
          </Drawer>
        </Hidden>

        <Hidden mdDown implementation="css">
          <Drawer
            PaperProps={{ sx: styles.drawerPaper }}
            variant="permanent"
            open
          >
            <Box
              sx={{ ...styles.toolbar, ...styles.switcher } as SxProps<Theme>}
            />
            <List component="nav">
              {items.map((item) => (
                <SidebarMenuItem
                  key={item.eventKey}
                  {...item}
                  styles={styles}
                  onNavigate={handleCloseDrawer}
                />
              ))}
            </List>
          </Drawer>
        </Hidden>
      </Box>

      <Box component="main" sx={styles.content}>
        <Box sx={styles.toolbar} />
        <Container>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Container>
      </Box>
    </Box>
  );
});

export default SidebarLayout;
